import { useEffect, useState } from 'react'
import {
  Drawer, Group, Avatar, Text, Box, Badge, CloseButton, Tabs,
} from '@mantine/core'
import type { UserRow } from './UsersTable'
import { UserDrawerGeneral } from './UserDrawerGeneral'
import { UserDrawerRoles } from './UserDrawerRoles'
import { AccessSummary } from './AccessSummary'
import { getKanidmLabel, getKanidmColor } from './kanidmStatus'
import { UserDrawerPermissions } from './UserDrawerPermissions'
import { UserDrawerOrganizations } from './UserDrawerOrganizations'
import { UserDrawerSecurity } from './UserDrawerSecurity'
import { UserDrawerPendingApproval } from './UserDrawerPendingApproval'

interface UserDrawerProps {
  user: UserRow | null
  onClose: () => void
  onActionComplete?: () => void | Promise<void>
}

function getInitials(name: string, email: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return email?.slice(0, 2).toUpperCase() ?? 'U'
}

// getKanidmLabel/getKanidmColor extraídos p/ ./kanidmStatus (C8 UX-B1, fonte única).

export function UserDrawer({ user, onClose, onActionComplete }: UserDrawerProps) {
  const [activeTab, setActiveTab] = useState<string>('geral')

  // Reset to "Geral" tab when user changes
  useEffect(() => {
    setActiveTab('geral')
  }, [user?.id])

  const isPending = user?.status === 'pending_approval'

  return (
    <Drawer
      opened={user !== null}
      onClose={onClose}
      position="right"
      size={480}
      withOverlay={false}
      withCloseButton={false}
      padding={0}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      {user && (
        <>
          {/* Header */}
          <Box
            p="md"
            style={{
              borderBottom: '1px solid var(--mantine-color-gray-3)',
            }}
          >
            <Group justify="space-between" align="flex-start">
              <Group gap="sm">
                <Avatar
                  size={48}
                  radius="md"
                  color={isPending ? 'yellow' : user.is_active ? 'inovareTeal' : 'gray'}
                  variant="filled"
                >
                  <Text style={{ fontSize: 14, fontWeight: 700 }}>
                    {getInitials(user.full_name, user.email)}
                  </Text>
                </Avatar>
                <Box>
                  <Text size="sm" fw={600} c="var(--ai-text-primary)">
                    {user.full_name || '—'}
                  </Text>
                  <Text size="xs" c="var(--ai-text-muted)">{user.email}</Text>
                  <Group gap={6} mt={4}>
                    {isPending ? (
                      <Badge size="xs" color="yellow" variant="dot">Pendente</Badge>
                    ) : (
                      <>
                        <Badge size="xs" color={user.is_active ? 'teal' : 'red'} variant="dot">
                          {user.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <Badge size="xs" color={getKanidmColor(user)} variant="light">
                          {getKanidmLabel(user)}
                        </Badge>
                      </>
                    )}
                  </Group>
                </Box>
              </Group>
              <CloseButton onClick={onClose} aria-label="Close drawer" />
            </Group>
          </Box>

          {/* Body: conditional on pending vs normal */}
          {isPending ? (
            <UserDrawerPendingApproval
              user={user}
              onClose={onClose}
              onActionComplete={() => {
                void onActionComplete?.()
              }}
            />
          ) : (
            <>
            <AccessSummary user={user} />
            <Tabs
              value={activeTab}
              onChange={(v) => setActiveTab(v ?? 'geral')}
              style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <Tabs.List px="md">
                <Tabs.Tab value="geral">Geral</Tabs.Tab>
                <Tabs.Tab value="roles">Funções</Tabs.Tab>
                <Tabs.Tab value="permissoes">Permissões</Tabs.Tab>
                <Tabs.Tab value="organizacoes">Organizações</Tabs.Tab>
                <Tabs.Tab value="seguranca">Segurança</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="geral" style={{ flex: 1, overflow: 'auto' }}>
                <UserDrawerGeneral user={user} onNavigateToOrgs={() => setActiveTab('organizacoes')} />
              </Tabs.Panel>

              <Tabs.Panel value="roles" style={{ flex: 1, overflow: 'auto' }}>
                <UserDrawerRoles user={user} />
              </Tabs.Panel>

              <Tabs.Panel value="permissoes" style={{ flex: 1, overflow: 'auto' }}>
                <UserDrawerPermissions user={user} />
              </Tabs.Panel>

              <Tabs.Panel value="organizacoes" style={{ flex: 1, overflow: 'auto' }}>
                <UserDrawerOrganizations user={user} />
              </Tabs.Panel>

              <Tabs.Panel value="seguranca" style={{ flex: 1, overflow: 'auto' }}>
                <UserDrawerSecurity user={user} onClose={onClose} />
              </Tabs.Panel>
            </Tabs>
            </>
          )}
        </>
      )}
    </Drawer>
  )
}
